import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { isUserAdminInSociety } from '../../../../helpers/society'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { useModal } from '../../../../hooks/useModal'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { Button, ButtonVariant } from '../../../common/Button'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { ToastType } from '../../../common/Toast/toast-type'
import { CreateUpdateHouseRuleModal } from '../CreateUpdateHouseRuleModal'
import { Linkify } from '../../../common/Linkify'
import { IHouseRuleModel } from '../../../../interfaces/models/house-rules.interfaces'
import { useAuth } from '../../../../hooks/useAuth'
import { deleteHouseRule } from '../../../../api/house-rules'

interface HouseRuleContentProps {
  houseRule: IHouseRuleModel
}

export const HouseRuleContent = ({
  houseRule,
}: HouseRuleContentProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { society } = useCurrentSociety()
  const { userId } = useAuth()
  const {
    show: showEditModal,
    open: openEditModal,
    close: closeEditModal,
  } = useModal()
  const {
    show: showConfirmationModal,
    open: openConfirmationModal,
    close: closeConfirmationModal,
  } = useModal()

  const isAdmin = isUserAdminInSociety(society, userId as string)

  const { mutate: mutateDeleteHouseRule } = useMutation({
    mutationFn: deleteHouseRule,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('createUpdateHouseRule.flashMessage.deleteHouseRuleSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('createUpdateHouseRule.flashMessage.deleteHouseRuleFailure')
      )
    },
  })

  const removeHouseRule = async (): Promise<void> => {
    mutateDeleteHouseRule(houseRule._id)
  }

  return (
    <>
      <div className="flex flex-col p-5">
        <Linkify>
          <p className="whitespace-pre-wrap break-words">{houseRule.rule}</p>
        </Linkify>
        {isAdmin && (
          <div className="mt-6 flex flex-row space-x-3">
            <Button
              onClick={openEditModal}
              label={translate('common.actions.edit')}
            />
            <Button
              variant={ButtonVariant.DANGER}
              onClick={openConfirmationModal}
              label={translate('common.actions.delete')}
            />
          </div>
        )}
      </div>
      {society && (
        <CreateUpdateHouseRuleModal
          society={society}
          houseRule={houseRule}
          show={showEditModal}
          close={closeEditModal}
        />
      )}
      <ConfirmationModal
        title={translate('createUpdateHouseRule.confirmationModal.title')}
        description={translate(
          'createUpdateHouseRule.confirmationModal.description'
        )}
        show={showConfirmationModal}
        close={closeConfirmationModal}
        onConfirm={removeHouseRule}
        deleteMode
      />
    </>
  )
}
